import * as React from "react"
import { MdArrowForward } from "react-icons/md"
import * as Yup from "yup"
import { Link, Button, InputConnectedField, Text } from "gatsby-interface"
import { Form, FormElement } from "@modules/form/components/Form"
import {
  cmsIntegration as cmsIntegrationText,
  ui as uiText,
} from "@modules/locales/default.js"
import { useInstallCosmicCmsIntegrationMutation } from "@modules/cms/shared/queries.generated"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { FormattedMessage } from "@modules/locales"

type FormValues = {
  authToken: string
}

const authTokenSchema = Yup.object().shape<FormValues>({
  authToken: Yup.string().required(
    cmsIntegrationText.messages.validationCosmicJsAuthTokenRequired
  ),
})

export type CosmicJsAuthFormProps = {
  siteId: string
  isProvisioning?: boolean
  onComplete: () => void
  onCancel: () => void
}

export function CosmicJsAuthForm({
  siteId,
  isProvisioning,
  onComplete,
  onCancel,
}: CosmicJsAuthFormProps) {
  const [authenticateCosmicCms] = useInstallCosmicCmsIntegrationMutation()
  const [setError, errorAlert] = useTriggerErrorAlert()

  return (
    <Form<FormValues>
      initialValues={{ authToken: `` }}
      validationSchema={authTokenSchema}
      onSubmit={values =>
        authenticateCosmicCms({
          variables: {
            authToken: values.authToken,
            siteId,
            provision: isProvisioning,
          },
          refetchQueries: [
            `cmsIntegrationsForSite`,
            `cmsIntegrationForProvisioning`,
          ],
        })
          .then(({ data }) => {
            if (data?.installCosmicCmsIntegration?.success) {
              return onComplete()
            }

            const errMesage = data?.installCosmicCmsIntegration?.message

            if (errMesage) {
              setError({ message: errMesage })
            }
          })
          .catch(err => setError({ message: err.message }))
      }
    >
      {() => (
        <FormElement noValidate applySpacing>
          <Text size="S" css={{ margin: 0 }}>
            <FormattedMessage<never, "link">
              message={
                cmsIntegrationText.messages.cosmicJsAuthTokenInstructions
              }
              tags={{
                link: function(content) {
                  return (
                    <Link
                      href={`https://app.cosmicjs.com/account/authentication`}
                      target="_blank"
                      css={{ textDecoration: `none` }}
                    >
                      {content}
                    </Link>
                  )
                },
              }}
            />
          </Text>
          <InputConnectedField
            label={cmsIntegrationText.labels.cosmicJsAuthToken}
            name="authToken"
            required
          />
          {errorAlert}
          <div
            css={{
              display: `flex`,
              alignItems: `center`,
              justifyContent: `space-between`,
            }}
          >
            <Button onClick={onCancel} tone="NEUTRAL" variant="SECONDARY">
              {uiText.actions.cancel}
            </Button>
            <Button
              type="submit"
              tone="BRAND"
              variant="PRIMARY"
              rightIcon={<MdArrowForward />}
            >
              {cmsIntegrationText.actions.authorize}
            </Button>
          </div>
        </FormElement>
      )}
    </Form>
  )
}
